import { Component, OnInit } from '@angular/core';
import { InvoiceModel } from 'app/models/invoice.model';
import { ActivatedRoute } from '@angular/router';
import { RegionModel } from 'app/core/models/region.model';
import { EstimatedReconciliationModel } from 'app/models/estimated-reconciliation.model';
import { NexusFormResultModel } from 'app/core/models/nexus-form-result.model';
import { NexusComponent } from 'app/core/components/nexus/nexus.component';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { EstimatedReconciliationService } from 'app/services/estimated-reconciliation.service';
import { TrueUpFeatureModel } from 'app/models/true-up.model';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent, ConfirmationDialogData } from './confirmation-dialog/confirmation-dialog.component';
import { TermsDialogComponent } from './terms-dialog/terms-dialog.component';

@Component({
    selector: 'app-estimated-reconciliation',
    templateUrl: './estimated-reconciliation.component.html',
    styleUrls: ['./estimated-reconciliation.component.scss'],
    standalone: false
})
export class EstimatedReconciliationComponent extends NexusComponent implements OnInit {

  public regions_model: Array<RegionModel>;
  public estimated_reconciliation_model: EstimatedReconciliationModel = new EstimatedReconciliationModel();
  public result_model: NexusFormResultModel = new NexusFormResultModel();
  public true_up_config_model: TrueUpFeatureModel;
  public showTermsError = false;
  public form = new UntypedFormGroup({
    address1: new UntypedFormControl('', Validators.required),
    address2: new UntypedFormControl(),
    city: new UntypedFormControl('', Validators.required),
    region_id: new UntypedFormControl('', Validators.required),
    zip5: new UntypedFormControl('', this.ValidateZip),
    use_alternate_recipient: new UntypedFormControl(false),
    first_name: new UntypedFormControl(null),
    last_name: new UntypedFormControl(null),
    agree: new UntypedFormControl(false, Validators.requiredTrue)
  });

  constructor(private route: ActivatedRoute, private estimated_reconciliation_service: EstimatedReconciliationService, private dialog: MatDialog) {
    super();
  }

  ngOnInit() {
    this.route.data.subscribe(({ regions_model, estimated_reconciliation_model,true_up_config_model }) => {
      this.regions_model = regions_model;
      this.estimated_reconciliation_model = estimated_reconciliation_model;
      this.true_up_config_model = true_up_config_model;
      this.form.patchValue(estimated_reconciliation_model);

      // Auto-enable checkbox if first_name and last_name are available
      if (estimated_reconciliation_model.first_name && estimated_reconciliation_model.last_name) {
        this.form.patchValue({ use_alternate_recipient: true });
      }

      if(this.estimated_reconciliation_model.created_date)
      {
        this.form.disable();
      }
    });
       // Set up alternate recipient field validators
    this.form.get('use_alternate_recipient')?.valueChanges.subscribe(useAlternate => {
      const alternateFields = ['first_name', 'last_name'];

      if (useAlternate) {
        alternateFields.forEach(field => {
          this.form.get(field)?.setValidators([Validators.required, this.noWhitespaceValidator]);
          this.form.get(field)?.updateValueAndValidity();
        });
      } else {
        alternateFields.forEach(field => {
          this.form.get(field)?.clearValidators();
          this.form.get(field)?.setValue(null); // Set to null when unchecked
          this.form.get(field)?.updateValueAndValidity();
        });
      }
    });

    // Apply validators if checkbox is initially enabled
    if (this.form.get('use_alternate_recipient')?.value) {
      const alternateFields = ['first_name', 'last_name'];
      alternateFields.forEach(field => {
        this.form.get(field)?.setValidators([Validators.required, this.noWhitespaceValidator]);
        this.form.get(field)?.updateValueAndValidity();
      });
    }

    this.form.valueChanges.subscribe((form: any) => {
      this.PopulateModelFromForm(form, this.estimated_reconciliation_model);
    });
  }
  onAgreeChange() {
    this.showTermsError = false;
    const agreeValue = this.form.get('agree')?.value;

    if (agreeValue) {
      this.openConfirmationDialog();
    }
  }
  openConfirmationDialog() {
    const useAlternate = this.form.get('use_alternate_recipient')?.value;
    const selectedRegion = this.regions_model.find(r =>
      r.id === this.form.get('region_id')?.value
    );    // Combine first and last name for alternate recipient
    let recipientName = undefined;
    if (useAlternate) {
      const firstName = (this.form.get('first_name')?.value || '').trim();
      const lastName = (this.form.get('last_name')?.value || '').trim();
      recipientName = `${firstName} ${lastName}`.trim();
      // If after trimming we only have empty string, set to undefined
      if (recipientName === '') {
        recipientName = undefined;
      }
    }
    else{
      const user = this.estimated_reconciliation_model.invoices.find(x=>x.customer).customer;
      recipientName = `${user.first_name} ${user.last_name}`.trim();
    }

    const dialogData: ConfirmationDialogData = {
      recipientName: recipientName,
      address1: this.form.get('address1')?.value,
      address2: this.form.get('address2')?.value,
      city: this.form.get('city')?.value,
      state: selectedRegion?.name || '',
      zip: this.form.get('zip5')?.value,
      isAlternateRecipient: useAlternate
    };    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '600px',
      height: 'auto',
      maxHeight: 'none',
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'no-scroll-dialog',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        // User cancelled - uncheck the agreement
        this.form.patchValue({ agree: false });
      }
    });
  }  openTermsDialog(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.dialog.open(TermsDialogComponent, {
      width: '700px',
      height: 'auto',
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'no-scroll-dialog'
    });
  }

  submit() {
    this.result_model.Reset();
    this.showTermsError = false;

    if(!this.form.get('agree')?.value)
    {
      this.showTermsError = true;
      this.result_model.Error('Please agree to the terms before submitting.');
      return;
    }

    if (this.form.invalid) {
      this.result_model.Incomplete();
      return;
    }

    this.StartSpinner();

    this.estimated_reconciliation_service.SaveReconciliation(this.estimated_reconciliation_model).subscribe(
      data => {
        this.estimated_reconciliation_model = data;
        this.form.patchValue(data);
        this.form.disable();
        this.result_model.Success('Reconciliation Requested!');
        this.StopSpinner();
      },
      err => {
        this.result_model.Error(err);
        this.StopSpinner();
      });
  }

    // Custom validator to prevent whitespace-only values
  noWhitespaceValidator(control: any) {
    const value = control.value;
    if (!value) {return null;} // Let required validator handle empty values
    if (typeof value === 'string' && value.trim().length === 0) {
      return { whitespace: true };
    }
    return null;
  }

  // Check if form is valid excluding the 'agree' checkbox
  get isFormValidExcludingAgree(): boolean {
    const addressValid = this.form.get('address1')?.valid;
    const cityValid = this.form.get('city')?.valid;
    const regionValid = this.form.get('region_id')?.valid;
    const zipValid = this.form.get('zip5')?.valid;

    // If alternate recipient is selected, check those fields too
    const useAlternate = this.form.get('use_alternate_recipient')?.value;
    let alternateFieldsValid = true;

    if (useAlternate) {
      alternateFieldsValid = this.form.get('first_name')?.valid && this.form.get('last_name')?.valid;
    }

    return addressValid && cityValid && regionValid && zipValid && alternateFieldsValid;
  }
}
